import { authFactory, type IAuthService, type AuthUser, type AuthResult } from '@rammetec/data-access'
import { rtLogService } from '@rammetec/core'
import { logActivity } from '@/services/logActivity'

/** Same ceiling as SC console / app-kit stale-build recovery — logout must not hang. */
const LOGOUT_FLUSH_MS = 1_000

/**
 * Thin façade over authFactory.getAuthService() — same pattern as the SC console.
 * The factory is configured by bootstrapDataAccess() before this is touched.
 */
class AuthService {
  private get service(): IAuthService {
    return authFactory.getAuthService()
  }

  getCurrentUser(): AuthUser | null {
    return this.service.getCurrentUser()
  }

  getIdToken(forceRefresh?: boolean): Promise<string | null> {
    return this.service.getIdToken(forceRefresh)
  }

  onAuthStateChanged(callback: (user: AuthUser | null) => void): () => void {
    return this.service.onAuthStateChanged(callback)
  }

  async login(email: string, password: string, rememberMe: boolean): Promise<AuthResult> {
    const started = Date.now()
    try {
      const result = await this.service.login(email, password, rememberMe)
      logActivity({
        action: 'session.login',
        summary: 'Entrou',
        durationMs: Date.now() - started,
      })
      return result
    } catch (error) {
      const reason =
        typeof error === 'object' && error != null && 'code' in error
          ? String((error as { code: unknown }).code)
          : 'unknown'
      logActivity({
        action: 'session.login',
        summary: 'Falhou ao entrar',
        outcome: 'error',
        durationMs: Date.now() - started,
        attrs: { reason },
      })
      throw error
    }
  }

  createUser(email: string, password: string): Promise<AuthResult> {
    return this.service.createUser(email, password)
  }

  async logout(): Promise<void> {
    logActivity({
      action: 'session.logout',
      summary: 'Saiu',
    })
    await Promise.race([
      rtLogService.flush({ keepalive: true }),
      new Promise<void>((resolve) => {
        setTimeout(resolve, LOGOUT_FLUSH_MS)
      }),
    ])
    return this.service.logout()
  }

  sendPasswordResetEmail(email: string): Promise<void> {
    return this.service.sendPasswordResetEmail(email)
  }
}

export const authService = new AuthService()
